import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, MailOpen } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import appApi from "./appApi";
import { Skeleton, stagger, rise } from "./bits";

const MessagesScreen = () => {
  const { t } = useLang();
  const m = t.mapp;
  const [messages, setMessages] = useState(null);

  const load = useCallback(() => {
    appApi.get("/app/messages").then((r) => setMessages(r.data)).catch(() => toast.error(m.error_generic));
  }, [m.error_generic]);
  useEffect(load, [load]);

  const open = async (msg) => {
    if (!msg.is_read) {
      try {
        await appApi.patch(`/app/messages/${msg.id}/read`);
        setMessages((prev) => prev.map((x) => (x.id === msg.id ? { ...x, is_read: true } : x)));
      } catch { /* silent */ }
    }
  };

  return (
    <div data-testid="app-messages-screen">
      <h1 className="font-heading text-3xl font-medium text-brand-ink">{m.tab_messages}</h1>
      {messages === null ? (
        <div className="mt-5 space-y-3"><Skeleton className="h-20" /><Skeleton className="h-20" /></div>
      ) : messages.length === 0 ? (
        <p className="text-sm text-brand-ink/40 italic py-10 text-center">{m.messages_empty}</p>
      ) : (
        <motion.div variants={stagger} initial="hidden" animate="show" className="mt-5 space-y-2.5">
          {messages.map((msg) => (
            <motion.details key={msg.id} variants={rise} className="rounded-2xl bg-white border border-brand/10 shadow-sm overflow-hidden" data-testid={`app-message-${msg.id}`}>
              <summary
                className="flex items-center gap-3 p-4 cursor-pointer list-none"
                onClick={() => open(msg)}
              >
                <div className={`grid place-items-center h-10 w-10 rounded-xl shrink-0 ${msg.is_read ? "bg-stone-100 text-stone-400" : "bg-brand-soft text-brand"}`}>
                  {msg.is_read ? <MailOpen className="h-5 w-5" /> : <Mail className="h-5 w-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <p className={`truncate text-sm ${msg.is_read ? "text-brand-ink/60" : "font-bold text-brand-ink"}`}>{msg.name}</p>
                  <p className="text-xs text-brand-ink/50 truncate">{msg.email}{msg.phone ? ` · ${msg.phone}` : ""}</p>
                </div>
                {!msg.is_read && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-brand text-brand-cream shrink-0">{m.msg_unread}</span>}
              </summary>
              <div className="px-4 pb-4 pt-1 text-sm text-brand-ink/80 whitespace-pre-wrap border-t border-brand/10">
                <p className="text-[11px] text-brand-ink/40 mb-1.5">{(msg.created_at || "").slice(0, 16).replace("T", " ")}</p>
                {msg.message}
                {msg.phone && <a href={`tel:${msg.phone}`} className="block mt-2 text-brand font-bold">{msg.phone}</a>}
              </div>
            </motion.details>
          ))}
        </motion.div>
      )}
    </div>
  );
};

export default MessagesScreen;
